
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useMutation, useQuery, useResult } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { AoAuditConfigTable } from "@26lights/orcha";

import HeaderBlock from "@/components/HeaderBlock.vue";

import {
  GET_FIDES_AUDIT_TABLE_CONFIGS,
  GET_ORCHA_AUDIT_TABLE_CONFIGS,
} from "@/graphql/auditConfig/queries";
import {
  UPDATE_FIDES_AUDIT_TABLE_CONFIG,
  UPDATE_ORCHA_AUDIT_TABLE_CONFIG,
} from "@/graphql/auditConfig/mutations";

import enUK from "@/locales/en-UK.json";

interface AuditColumn {
  name: string;
  isPrimaryKey: boolean;
}

interface AuditTableConfig {
  schemaName: string;
  tableName: string;
  isEnabled: boolean;
  excludeColumns: string[];
  columns: AuditColumn[];
}

interface AuditConfigUpdate {
  schemaName: string;
  tableName: string;
  isEnabled: boolean;
  excludeColumns: string[];
}

type Backend = "orcha" | "fides";

type MessageSchema = typeof enUK;

export default defineComponent({
  name: "AdminAudit",

  components: {
    HeaderBlock,
    AoAuditConfigTable,
  },

  setup() {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const toast = useToast();
    const searchTerm = ref("");

    const orchaError = ref<string | null>(null);
    const fidesLoadError = ref<string | null>(null);
    const fidesUpdateError = ref<string | null>(null);

    const {
      result: orchaResult,
      loading: orchaLoading,
      onError: onOrchaError,
      refetch: refetchOrcha,
    } = useQuery<{
      auditTableConfigs: AuditTableConfig[];
    }>(GET_ORCHA_AUDIT_TABLE_CONFIGS, null, {
      fetchPolicy: "cache-and-network",
    });

    onOrchaError((error) => {
      logErrorMessages(error);
      orchaError.value = error.message;
      toast.error(t("audit.errors.loadOrcha"));
    });

    const orchaTables = useResult(
      orchaResult,
      [] as AuditTableConfig[],
      (data) => data?.auditTableConfigs ?? [],
    );

    const {
      result: fidesResult,
      loading: fidesLoading,
      onError: onFidesError,
      refetch: refetchFides,
    } = useQuery<{
      fidesAuditTableConfigs: AuditTableConfig[];
    }>(GET_FIDES_AUDIT_TABLE_CONFIGS, null, {
      fetchPolicy: "cache-and-network",
    });

    onFidesError((error) => {
      logErrorMessages(error);
      fidesLoadError.value = error.message;
      console.warn("Fides audit configuration is unavailable", error);
    });

    const fidesTables = useResult(
      fidesResult,
      [] as AuditTableConfig[],
      (data) => data?.fidesAuditTableConfigs ?? [],
    );

    const fidesError = computed(
      () => fidesLoadError.value ?? fidesUpdateError.value,
    );

    const fidesNotAvailable = computed(() => Boolean(fidesLoadError.value));

    const summaryCounts = computed(() => {
      const all = [...orchaTables.value, ...fidesTables.value];

      const enabled = all.filter((table) => table.isEnabled).length;

      const disabled = all.length - enabled;

      return {
        enabled,
        disabled,
      };
    });

    const filtered = (tables: AuditTableConfig[]): AuditTableConfig[] => {
      const term = searchTerm.value.trim().toLowerCase();

      if (!term) {
        return tables;
      }

      return tables.filter((row) => row.tableName.toLowerCase().includes(term));
    };

    const { mutate: updateOrcha, onError: onUpdateOrchaError } = useMutation<{
      updateAuditTableConfig: AuditTableConfig;
    }>(UPDATE_ORCHA_AUDIT_TABLE_CONFIG);

    const { mutate: updateFides, onError: onUpdateFidesError } = useMutation<{
      updateFidesAuditTableConfig: AuditTableConfig;
    }>(UPDATE_FIDES_AUDIT_TABLE_CONFIG);

    onUpdateOrchaError((error) => {
      logErrorMessages(error);
      orchaError.value = error.message;

      toast.error(
        t("audit.errors.updateOrcha", {
          message: error.message,
        }),
      );

      refetchOrcha();
    });

    onUpdateFidesError((error) => {
      logErrorMessages(error);
      fidesUpdateError.value = error.message;

      toast.error(
        t("audit.errors.updateFides", {
          message: error.message,
        }),
      );

      refetchFides();
    });

    async function onUpdate(
      backend: Backend,
      update: AuditConfigUpdate,
    ): Promise<void> {
      try {
        if (backend === "orcha") {
          await updateOrcha(update);
          await refetchOrcha();
          orchaError.value = null;
          return;
        }

        await updateFides(update);
        await refetchFides();
        fidesLoadError.value = null;
        fidesUpdateError.value = null;
      } catch {
        // Errors are already handled by the mutation onError callbacks.
      }
    }

    return {
      t,
      searchTerm,
      orchaTables,
      orchaLoading,
      orchaError,
      fidesTables,
      fidesLoading,
      fidesError,
      fidesNotAvailable,
      summaryCounts,
      filtered,
      onUpdate,
    };
  },
});
